import { useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { walletManager } from '../components/EthersSetup.js';

/**
 * Robust blockchain event listening hook with automatic fallback
 * 
 * Each game component uses this independently for resilient event handling:
 * - Real-time Ethers.js event listening as primary method
 * - Automatic fallback to periodic event querying on connection issues
 * - Connection monitoring and auto-recovery
 * - Proper cleanup and memory management
 * - Error handling with graceful degradation
 * 
 * @param {Object} config - Event configuration
 * @param {Object} config.contract - Ethers.js contract instance
 * @param {string} config.gameId - Game identifier for filtering
 * @param {Array} config.events - Array of event configurations
 *   Each event: { name: 'EventName', filter: [gameId], handler: callbackFn }
 * @param {boolean} config.enabled - Enable/disable event listening
 * @param {number} config.fallbackInterval - Fallback polling interval (default: 5000ms)
 * @param {number} config.connectionTimeout - Connection health check timeout (default: 30000ms)
 * 
 * @returns {Object} - Event listening state and control functions
 */
const useBlockchainEvents = ({
  contract,
  gameId,
  events = [],
  enabled = true,
  fallbackInterval,
  connectionTimeout
}) => {
  // Get optimal configuration from active wallet provider
  const getOptimalConfig = () => {
    try {
      const activeProvider = walletManager?.getActiveProvider?.();
      if (activeProvider && typeof activeProvider.getEventConfig === 'function') {
        const config = activeProvider.getEventConfig();
        return config;
      }
    } catch (error) {
      console.warn('[useBlockchainEvents] Error getting wallet config, using defaults:', error.message);
    }

    // Default configuration if wallet manager is not available
    return {
      fallbackInterval: 15000, // Reduced frequency to respect API rate limits
      connectionTimeout: 30000,
      supportsRealTimeEvents: true
    };
  };

  const optimalConfig = getOptimalConfig();
  
  // Use provided config or fall back to optimal config
  const actualFallbackInterval = fallbackInterval ?? optimalConfig.fallbackInterval;
  const actualConnectionTimeout = connectionTimeout ?? optimalConfig.connectionTimeout;
  const [state, setState] = useState({
    isConnected: false,
    isListening: false,
    errorCount: 0,
    lastEventTime: 0,
    mode: 'real-time', // 'real-time' or 'fallback'
    isInitialized: false // Track if event listeners have been set up
  });

  // Refs to maintain stable references across re-renders
  const listenersRef = useRef([]);
  const fallbackTimerRef = useRef(null);
  const connectionCheckRef = useRef(null);
  const lastCheckedBlockRef = useRef(0);
  const mountedRef = useRef(true);

  // Cleanup function to remove all listeners and timers
  const cleanup = () => {
    // Remove event listeners
    listenersRef.current.forEach(({ contract: listenerContract, filter, handler }) => {
      try {
        if (listenerContract && typeof listenerContract.off === 'function') {
          listenerContract.off(filter, handler);
        }
      } catch (error) {
        console.warn('[useBlockchainEvents] Cleanup warning:', error.message);
      }
    });
    listenersRef.current = [];

    // Clear timers
    if (fallbackTimerRef.current) {
      clearInterval(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    if (connectionCheckRef.current) {
      clearTimeout(connectionCheckRef.current);
      connectionCheckRef.current = null;
    }
  };

  // Update state safely (only if component is mounted)
  const safeSetState = (updater) => {
    if (mountedRef.current) {
      setState(updater);
    }
  };

  // Check provider connection health
  const checkConnection = async () => {
    if (!contract || !contract.provider) return false;

    try {
      await contract.provider.getBlockNumber();
      return true;
    } catch (error) {
      console.warn('[useBlockchainEvents] Connection check failed:', error.message);
      return false;
    }
  };

  // Fallback event checking using queryFilter
  const checkEventsViaQuery = async () => {
    if (!contract || !enabled) return;

    try {
      const currentBlock = await contract.provider.getBlockNumber();
      const fromBlock = Math.max(lastCheckedBlockRef.current, currentBlock - 9); // Limit to 10 blocks max for Alchemy free tier
      
      for (const eventConfig of events) {
        const { name: eventName, filter, handler } = eventConfig;
        
        try {
          // Validate contract has filters and the specific event exists
          if (!contract.filters) {
            console.warn(`[useBlockchainEvents] Contract missing filters property for ${eventName} in fallback`);
            continue;
          }
          
          if (typeof contract.filters[eventName] !== 'function') {
            console.warn(`[useBlockchainEvents] Event ${eventName} not found in contract filters in fallback`);
            continue;
          }
          
          // Create event filter and query
          const eventFilter = contract.filters[eventName](...filter);
          const eventLogs = await contract.queryFilter(eventFilter, fromBlock, 'latest');
          
          if (eventLogs.length > 0) {
            console.log(`[useBlockchainEvents] Found ${eventLogs.length} ${eventName} events via fallback`);
            
            // Process each event
            for (const event of eventLogs) {
              if (handler && typeof handler === 'function') {
                try {
                  await handler(event);
                } catch (handlerError) {
                  console.error('[useBlockchainEvents] Event handler error:', handlerError);
                }
              }
            }
          }
        } catch (eventError) {
          console.warn(`[useBlockchainEvents] Failed to query ${eventName}:`, eventError.message);
        }
      }
      
      lastCheckedBlockRef.current = currentBlock;
      
      safeSetState(prev => ({ 
        ...prev, 
        lastEventTime: Date.now(),
        errorCount: Math.max(0, prev.errorCount - 1) // Reduce error count on successful query
      }));
      
    } catch (error) {
      console.warn('[useBlockchainEvents] Fallback query failed:', error.message);
      safeSetState(prev => ({ 
        ...prev, 
        errorCount: prev.errorCount + 1 
      }));
    }
  };

  // Set up real-time event listeners
  const setupRealTimeListeners = () => {
    if (!contract || !enabled || events.length === 0) return;
    if (listenersRef.current.length > 0) return; // Prevent duplicate setup

    console.log(`[useBlockchainEvents] Setting up real-time listeners for ${events.length} events`);

    events.forEach((eventConfig) => {
      const { name: eventName, filter, handler } = eventConfig;
      
      try {
        // Validate contract has filters and the specific event exists
        if (!contract.filters) {
          console.warn(`[useBlockchainEvents] Contract missing filters property for ${eventName}`);
          return;
        }
        
        if (typeof contract.filters[eventName] !== 'function') {
          console.warn(`[useBlockchainEvents] Event ${eventName} not found in contract filters`);
          return;
        }
        
        const eventFilter = contract.filters[eventName](...filter);
        
        const wrappedHandler = async (...args) => {
          try {
            console.log(`[useBlockchainEvents] Real-time ${eventName} event received`);
            safeSetState(prev => ({ 
              ...prev, 
              lastEventTime: Date.now(),
              errorCount: Math.max(0, prev.errorCount - 1)
            }));
            
            if (handler && typeof handler === 'function') {
              await handler(...args);
            }
          } catch (handlerError) {
            console.error(`[useBlockchainEvents] Handler error for ${eventName}:`, handlerError);
            safeSetState(prev => ({ ...prev, errorCount: prev.errorCount + 1 }));
          }
        };

        contract.on(eventFilter, wrappedHandler);
        
        // Store reference for cleanup
        listenersRef.current.push({
          contract,
          filter: eventFilter,
          handler: wrappedHandler
        });
        
      } catch (error) {
        console.warn(`[useBlockchainEvents] Failed to set up ${eventName} listener:`, error.message);
        safeSetState(prev => ({ ...prev, errorCount: prev.errorCount + 1 }));
      }
    });

    safeSetState(prev => ({ ...prev, isListening: true, mode: 'real-time', isInitialized: true }));
  };

  // Set up fallback polling
  const setupFallbackPolling = () => {
    if (fallbackTimerRef.current) return; // Already set up

    console.log(`[useBlockchainEvents] Setting up fallback polling (${actualFallbackInterval}ms interval)`);
    
    fallbackTimerRef.current = setInterval(() => {
      checkEventsViaQuery();
    }, actualFallbackInterval);

    safeSetState(prev => ({ ...prev, isListening: true, mode: 'fallback', isInitialized: true }));
  };

  // Main effect - set up event listening strategy
  useEffect(() => {
    if (!contract || !enabled || events.length === 0) {
      cleanup();
      safeSetState({ isConnected: false, isListening: false, errorCount: 0, lastEventTime: 0, mode: 'real-time', isInitialized: false });
      return;
    }

    // Prevent re-initialization if already initialized with same contract and config
    if (state.isInitialized && listenersRef.current.length > 0 &&
        listenersRef.current.length === events.length) {
      return;
    }

    let connectionCheckTimer;

    const initializeListening = async () => {
      const isConnected = await checkConnection();
      
      safeSetState(prev => ({ ...prev, isConnected }));

      if (isConnected) {
        // Try real-time listeners first
        setupRealTimeListeners();
        
        // Set up periodic connection health checks
        const scheduleConnectionCheck = () => {
          connectionCheckTimer = setTimeout(async () => {
            const stillConnected = await checkConnection();
            
            if (!stillConnected && mountedRef.current) {
              console.warn('[useBlockchainEvents] Connection lost, switching to fallback mode');
              cleanup(); // Remove real-time listeners
              setupFallbackPolling();
              safeSetState(prev => ({ ...prev, isConnected: false, mode: 'fallback' }));
            }
            
            if (mountedRef.current) {
              scheduleConnectionCheck(); // Schedule next check
            }
          }, actualConnectionTimeout);
        };
        
        scheduleConnectionCheck();
        
      } else {
        // Connection failed, use fallback immediately
        setupFallbackPolling();
      }
    };

    initializeListening();

    return () => {
      cleanup();
      if (connectionCheckTimer) clearTimeout(connectionCheckTimer);
    };
  }, [contract, gameId, enabled, events.length, actualFallbackInterval, actualConnectionTimeout]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, []);

  // Expose control functions and state
  return {
    // State information
    isConnected: state.isConnected,
    isListening: state.isListening,
    mode: state.mode,
    errorCount: state.errorCount,
    lastEventTime: state.lastEventTime,
    
    // Control functions
    forceRefresh: checkEventsViaQuery,
    
    // Health check
    isHealthy: state.isListening && state.errorCount < 5
  };
};

export default useBlockchainEvents;